import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, RefreshCw, Download, Trash2, Filter, ChevronLeft, ChevronRight,
  Mail, Phone, Calendar, MapPin, Camera, FileText, Search, X,
  BarChart3, Clock, CheckCircle2, AlertCircle, Inbox, Activity, Shield
} from "lucide-react";
import axios from "axios";
import AnalyticsTab from "../components/admin/AnalyticsTab";
import HealthTab from "../components/admin/HealthTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authH() { return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }; }

const STATUS_MAP = {
  new: { label: "Nouveau", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", dot: "bg-blue-400" },
  in_progress: { label: "En cours", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", dot: "bg-amber-400" },
  done: { label: "Traité", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", dot: "bg-emerald-400" },
};
const TYPE_MAP = {
  contact: { label: "Contact", color: "bg-slate-500/10 text-slate-300" },
  diogene: { label: "Diogène", color: "bg-purple-500/10 text-purple-300" },
  deces: { label: "Décès", color: "bg-rose-500/10 text-rose-300" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.new;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span>;
}
function TypeBadge({ type }) {
  const t = TYPE_MAP[type] || TYPE_MAP.contact;
  return <span className={`px-2.5 py-1 rounded text-xs font-medium ${t.color}`}>{t.label}</span>;
}

const TABS = [
  { id: "contacts", label: "Demandes", icon: Inbox },
  { id: "analytics", label: "Analytics", icon: Activity },
  { id: "health", label: "Santé du site", icon: Shield },
];

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("contacts");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/stats`, { headers: authH() }); setStats(data); }
    catch (err) { if (err.response?.status === 401) { onLogout(); navigate("/admin"); } }
  }, [onLogout, navigate]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(`${API}/admin/contacts`, { headers: authH(), params });
      setContacts(data.contacts); setTotal(data.total); setPages(data.pages);
    } catch (err) { if (err.response?.status === 401) { onLogout(); navigate("/admin"); } }
    finally { setLoading(false); }
  }, [page, filterType, filterStatus, onLogout, navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/contacts/${id}/status`, { status }, { headers: authH() });
    fetchContacts(); fetchStats();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Supprimer cette demande définitivement ?")) return;
    await axios.delete(`${API}/admin/contacts/${id}`, { headers: authH() });
    setSelected(null); fetchContacts(); fetchStats();
  };
  const purgeTest = async () => {
    if (!window.confirm("Supprimer toutes les données de test ?")) return;
    setPurging(true);
    try {
      const { data } = await axios.delete(`${API}/admin/contacts-purge-test`, { headers: authH() });
      alert(`${data.deleted} entrée(s) de test supprimée(s).`);
      fetchContacts(); fetchStats();
    } finally { setPurging(false); }
  };
  const exportCSV = async () => {
    try {
      const res = await fetch(`${API}/admin/export-csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts-ign.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export error:", e);
    }
  };
  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("admin_token"); onLogout(); navigate("/admin");
  };

  const filteredContacts = search
    ? contacts.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search))
    : contacts;

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A2540]">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-5 h-5 text-white/60" />
            <h1 className="text-base font-bold text-white font-['Outfit']">IGN Services</h1>
            <span className="text-white/20 text-xs font-['Manrope'] hidden sm:block">Administration</span>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "contacts" && (
              <>
                <button data-testid="export-csv-btn" onClick={exportCSV} className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/60 px-2.5 py-1.5 rounded text-[11px] hover:bg-white/10 transition font-['Manrope']">
                  <Download className="w-3 h-3" /> CSV
                </button>
                <button data-testid="purge-test-btn" onClick={purgeTest} disabled={purging} className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 text-red-400 px-2.5 py-1.5 rounded text-[11px] hover:bg-red-500/20 transition font-['Manrope'] disabled:opacity-50">
                  <Trash2 className="w-3 h-3" /> Purger
                </button>
              </>
            )}
            <button data-testid="logout-btn" onClick={handleLogout} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition text-[11px] font-['Manrope'] ml-2">
              <LogOut className="w-3.5 h-3.5" /> Déconnexion
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-['Manrope'] font-medium border-b-2 transition ${
                  activeTab === tab.id ? "text-white border-white" : "text-white/40 border-transparent hover:text-white/60"
                }`}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* CONTACTS TAB */}
        {activeTab === "contacts" && (
          <div className="space-y-5">
            {stats && (
              <div data-testid="stats-section" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { icon: Inbox, label: "Total", value: stats.total, accent: "bg-white/10 text-white" },
                  { icon: AlertCircle, label: "Nouveaux", value: stats.nouveau, accent: "bg-blue-500/10 text-blue-400" },
                  { icon: Clock, label: "En cours", value: stats.en_cours, accent: "bg-amber-500/10 text-amber-400" },
                  { icon: CheckCircle2, label: "Traités", value: stats.traite, accent: "bg-emerald-500/10 text-emerald-400" },
                ].map((s, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.accent}`}><s.icon className="w-5 h-5" /></div>
                    <div><p className="text-xl font-bold text-white font-['Outfit']">{s.value}</p><p className="text-white/40 text-[11px] uppercase tracking-wider font-['Manrope']">{s.label}</p></div>
                  </div>
                ))}
              </div>
            )}

            <div data-testid="filters-section" className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input data-testid="search-input" type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..."
                  className="w-full bg-white/5 border border-white/10 text-white pl-10 pr-4 py-2 rounded text-sm focus:border-white/30 focus:outline-none transition font-['Manrope'] placeholder:text-white/20" />
              </div>
              <Filter className="w-4 h-4 text-white/30" />
              <select data-testid="filter-type" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                className="bg-white/5 border border-white/10 text-white/70 px-3 py-2 rounded text-xs font-['Manrope'] focus:outline-none">
                <option value="">Tous types</option><option value="contact">Contact</option><option value="diogene">Diogène</option><option value="deces">Décès</option>
              </select>
              <select data-testid="filter-status" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="bg-white/5 border border-white/10 text-white/70 px-3 py-2 rounded text-xs font-['Manrope'] focus:outline-none">
                <option value="">Tous statuts</option><option value="new">Nouveau</option><option value="in_progress">En cours</option><option value="done">Traité</option>
              </select>
              <button data-testid="refresh-btn" onClick={() => { fetchContacts(); fetchStats(); }} className="bg-white/5 border border-white/10 text-white/50 p-2 rounded hover:bg-white/10 transition">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-5">
              <div className="flex-1 min-w-0">
                <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                  {loading ? <div className="p-12 text-center text-white/30 font-['Manrope']">Chargement...</div> :
                   filteredContacts.length === 0 ? <div className="p-12 text-center text-white/30 font-['Manrope']">Aucune demande</div> : (
                    <div className="overflow-x-auto">
                      <table data-testid="contacts-table" className="w-full">
                        <thead><tr className="border-b border-white/10">
                          {["Date", "Client", "Type", "Statut", "Actions"].map(h => (
                            <th key={h} className="text-left px-4 py-2.5 text-white/40 text-[11px] uppercase tracking-wider font-['Manrope'] font-medium">{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {filteredContacts.map(c => (
                            <tr key={c.id} data-testid={`contact-row-${c.id}`} onClick={() => setSelected(c)}
                              className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition ${selected?.id === c.id ? "bg-white/5" : ""}`}>
                              <td className="px-4 py-2.5 text-white/50 text-xs font-['Manrope'] whitespace-nowrap">{formatDate(c.created_at)}</td>
                              <td className="px-4 py-2.5"><p className="text-white text-sm font-medium font-['Manrope']">{c.name}</p><p className="text-white/40 text-xs font-['Manrope']">{c.email}</p></td>
                              <td className="px-4 py-2.5"><TypeBadge type={c.type} /></td>
                              <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                  {c.status !== "done" && <button data-testid={`mark-done-${c.id}`} onClick={(e) => { e.stopPropagation(); updateStatus(c.id, "done"); }}
                                    className="p-1.5 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition" title="Traité"><CheckCircle2 className="w-3.5 h-3.5" /></button>}
                                  {c.status === "new" && <button data-testid={`mark-progress-${c.id}`} onClick={(e) => { e.stopPropagation(); updateStatus(c.id, "in_progress"); }}
                                    className="p-1.5 rounded bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition" title="En cours"><Clock className="w-3.5 h-3.5" /></button>}
                                  <button data-testid={`delete-${c.id}`} onClick={(e) => { e.stopPropagation(); deleteContact(c.id); }}
                                    className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {pages > 1 && (
                    <div data-testid="pagination" className="flex items-center justify-between px-4 py-2.5 border-t border-white/10">
                      <span className="text-white/30 text-xs font-['Manrope']">{total} — page {page}/{pages}</span>
                      <div className="flex gap-1">
                        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded bg-white/5 text-white/50 hover:bg-white/10 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                        <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded bg-white/5 text-white/50 hover:bg-white/10 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selected && (
                <div data-testid="contact-detail" className="w-80 shrink-0 bg-white/5 border border-white/10 rounded-lg p-5 space-y-4 self-start sticky top-28">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-bold font-['Outfit'] text-sm">Détail</h3>
                    <button onClick={() => setSelected(null)} className="text-white/30 hover:text-white/60"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex items-center gap-2"><TypeBadge type={selected.type} /><StatusBadge status={selected.status} /></div>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-white/30" /><span className="text-white/60 font-['Manrope']">{formatDate(selected.created_at)}</span></div>
                    <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-white/30" /><span className="text-white font-medium font-['Manrope']">{selected.name}</span></div>
                    <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-white/30" /><a href={`mailto:${selected.email}`} className="text-blue-400 hover:underline font-['Manrope'] text-xs">{selected.email}</a></div>
                    {selected.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-white/30" /><a href={`tel:${selected.phone}`} className="text-blue-400 hover:underline font-['Manrope'] text-xs">{selected.phone}</a></div>}
                    {(selected.service || selected.situation) && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/30" /><span className="text-white/60 font-['Manrope'] text-xs">{selected.service || selected.situation}</span></div>}
                    {selected.surface && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-white/30" /><span className="text-white/60 font-['Manrope'] text-xs">Surface : {selected.surface}</span></div>}
                  </div>
                  <div>
                    <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 font-['Manrope']">Message</p>
                    <p className="text-white/70 text-xs leading-relaxed bg-white/5 rounded p-2.5 font-['Manrope'] whitespace-pre-wrap">{selected.message || selected.description}</p>
                  </div>
                  {selected.photos?.length > 0 && (
                    <div>
                      <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5 font-['Manrope']"><Camera className="w-3 h-3 inline mr-1" />{selected.photos.length} photo(s)</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {selected.photos.map((p, i) => (
                          <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer" className="block bg-white/5 border border-white/10 rounded p-1.5 text-center hover:bg-white/10 transition">
                            <Camera className="w-5 h-5 text-white/30 mx-auto" /><span className="text-white/40 text-[10px] font-['Manrope']">Photo {i + 1}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-1.5 pt-1">
                    {selected.status !== "in_progress" && <button data-testid="detail-mark-progress" onClick={() => updateStatus(selected.id, "in_progress")} className="flex-1 flex items-center justify-center gap-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 py-1.5 rounded text-[11px] font-medium hover:bg-amber-500/20 transition font-['Manrope']"><Clock className="w-3 h-3" />En cours</button>}
                    {selected.status !== "done" && <button data-testid="detail-mark-done" onClick={() => updateStatus(selected.id, "done")} className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 py-1.5 rounded text-[11px] font-medium hover:bg-emerald-500/20 transition font-['Manrope']"><CheckCircle2 className="w-3 h-3" />Traité</button>}
                    <button data-testid="detail-delete" onClick={() => deleteContact(selected.id)} className="flex items-center justify-center bg-red-500/10 border border-red-500/20 text-red-400 py-1.5 px-2.5 rounded text-[11px] hover:bg-red-500/20 transition"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === "analytics" && <AnalyticsTab />}

        {/* HEALTH TAB */}
        {activeTab === "health" && <HealthTab />}
      </div>
    </div>
  );
}
